/**
 * Ansvariga / Kontaktroller per organisation
 * Listar roller (system + egna), användardropdown per roll, och hantering av egna roller
 */

import { useEffect, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import type { EntityId } from '@/types/ids';
import { userSelectLabel } from '@/utils/userSearch';

interface SelectUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  rsid?: string | null;
  employee_id?: string | null;
}

export interface ContactRoleDefinition {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_system: number;
  organization_id: number | null;
  display_order: number;
}

export interface ContactRoleAssignment {
  id: number;
  organization_id: number;
  role_id: number;
  role_code: string;
  role_name: string;
  user_id: string;
  user_email: string;
  user_first_name: string | null;
  user_last_name: string | null;
}

interface OrganizationContactRolesProps {
  organizationId: EntityId;
}

export default function OrganizationContactRoles({ organizationId }: OrganizationContactRolesProps) {
  const { t } = useTranslation();
  const [definitions, setDefinitions] = useState<ContactRoleDefinition[]>([]);
  const [assignments, setAssignments] = useState<ContactRoleAssignment[]>([]);
  const [users, setUsers] = useState<SelectUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRoleId, setSavingRoleId] = useState<number | null>(null);
  const [showAddCustomRole, setShowAddCustomRole] = useState(false);
  const [customRoleCode, setCustomRoleCode] = useState('');
  const [customRoleName, setCustomRoleName] = useState('');
  const [customRoleDescription, setCustomRoleDescription] = useState('');
  const [submittingCustom, setSubmittingCustom] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const load = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const [defRes, assignRes, usersRes] = await Promise.all([
        api.get<{ success: boolean; data: ContactRoleDefinition[] }>(
          `/api/organizations/${organizationId}/contact-role-definitions`
        ),
        api.get<{ success: boolean; data: ContactRoleAssignment[] }>(
          `/api/organizations/${organizationId}/contact-roles`
        ),
        api.get<{ success: boolean; data: SelectUser[] }>(
          `/api/users/for-select?organizationId=${organizationId}&includeSubOrgs=true`
        ),
      ]);
      if (defRes.data.success) setDefinitions(defRes.data.data);
      const loadedAssignments = assignRes.data.success ? assignRes.data.data : [];
      if (assignRes.data.success) setAssignments(loadedAssignments);

      const fromSelect = usersRes.data.success ? usersRes.data.data : [];
      const byId = new Map(fromSelect.map((u) => [u.id, u]));
      for (const a of loadedAssignments) {
        if (a.user_id && !byId.has(a.user_id)) {
          byId.set(a.user_id, {
            id: a.user_id,
            first_name: a.user_first_name,
            last_name: a.user_last_name,
            email: a.user_email,
          });
        }
      }
      setUsers(Array.from(byId.values()));
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? 'Kunde inte ladda kontaktroller');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [organizationId]);

  const assignmentByRoleId = Object.fromEntries(assignments.map((a) => [a.role_id, a]));

  const handleSetContactRole = async (roleId: number, userId: string | null) => {
    setSavingRoleId(roleId);
    try {
      const res = await api.put<{ success: boolean; data: ContactRoleAssignment | null }>(
        `/api/organizations/${organizationId}/contact-roles`,
        { roleId, userId }
      );
      if (res.data.success) {
        await load();
        toast.success(t('common.saved') ?? 'Sparat');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? 'Kunde inte spara');
    } finally {
      setSavingRoleId(null);
    }
  };

  const displayName = (def: ContactRoleDefinition) => {
    const key = def.name && def.name.startsWith('organizations.') ? def.name : null;
    return key ? t(key) : def.name;
  };

  const handleCreateCustomRole = async () => {
    const code = customRoleCode.trim();
    const name = customRoleName.trim();
    if (!code) {
      toast.error(t('organizations.contactRoles.codeRequired') ?? 'Kod krävs');
      return;
    }
    setSubmittingCustom(true);
    try {
      await api.post(`/api/organizations/${organizationId}/contact-role-definitions`, {
        code,
        name: name || code,
        description: customRoleDescription.trim() || undefined,
      });
      toast.success(t('organizations.contactRoles.roleCreated') ?? 'Roll skapad');
      setShowAddCustomRole(false);
      setCustomRoleCode('');
      setCustomRoleName('');
      setCustomRoleDescription('');
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? 'Kunde inte skapa roll');
    } finally {
      setSubmittingCustom(false);
    }
  };

  const handleUpdateCustomRole = async (roleId: number) => {
    try {
      await api.put(`/api/organizations/${organizationId}/contact-role-definitions/${roleId}`, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      toast.success(t('common.saved') ?? 'Sparat');
      setEditingRoleId(null);
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? 'Kunde inte spara');
    }
  };

  const handleDeleteCustomRole = async (roleId: number) => {
    if (!confirm(t('organizations.contactRoles.confirmDeleteRole') ?? 'Ta bort denna roll?')) return;
    try {
      await api.delete(`/api/organizations/${organizationId}/contact-role-definitions/${roleId}`);
      toast.success(t('organizations.contactRoles.roleDeleted') ?? 'Roll borttagen');
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? 'Kunde inte ta bort');
    }
  };

  if (loading) {
    return (
      <div className="text-textTertiary text-sm py-2">{t('common.loading')}</div>
    );
  }

  const systemRoles = definitions.filter((d) => d.is_system === 1);
  const customRoles = definitions.filter((d) => d.is_system === 0);

  return (
    <div className="mb-6 p-4 rounded-lg border border-border bg-bgElevated">
      <h3 className="text-sm font-medium text-textDefault mb-3">
        {t('organizations.contactRoles.title')}
      </h3>

      <div className="space-y-3">
        {systemRoles.map((def) => {
          const assign = assignmentByRoleId[def.id];
          const saving = savingRoleId === def.id;
          return (
            <div key={def.id} className="flex items-center gap-3 flex-wrap">
              <label className="w-40 text-sm text-textDefault shrink-0">
                {displayName(def)}
              </label>
              <select
                value={assign?.user_id ?? ''}
                onChange={(e) =>
                  handleSetContactRole(def.id, e.target.value || null)
                }
                disabled={saving}
                className="flex-1 min-w-[180px] px-2 py-1.5 border border-border rounded bg-bgContainer text-textDefault text-sm"
              >
                <option value="">{t('organizations.contactRoles.noAssignment')}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {userSelectLabel(u)}
                    {u.email && (u.first_name || u.last_name) ? ` (${u.email})` : ''}
                  </option>
                ))}
              </select>
              {saving && <span className="text-xs text-textTertiary">{t('common.loading')}</span>}
            </div>
          );
        })}

        {customRoles.map((def) => {
          const assign = assignmentByRoleId[def.id];
          const saving = savingRoleId === def.id;
          const isEditing = editingRoleId === def.id;
          return (
            <div key={def.id} className="flex items-center gap-3 flex-wrap border-t border-border pt-2 mt-2">
              {isEditing ? (
                <>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t('organizations.contactRoles.roleName')}
                    className="w-40 px-2 py-1 border border-border rounded text-sm"
                  />
                  <input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder={t('organizations.contactRoles.description')}
                    className="flex-1 min-w-[120px] px-2 py-1 border border-border rounded text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleUpdateCustomRole(def.id)}
                    className="px-2 py-1 text-sm bg-bgPrimary text-textLight rounded"
                  >
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRoleId(null);
                      setEditName('');
                      setEditDescription('');
                    }}
                    className="px-2 py-1 text-sm bg-bgLight text-textDefault rounded"
                  >
                    {t('common.cancel')}
                  </button>
                </>
              ) : (
                <>
                  <label className="w-40 text-sm text-textDefault shrink-0">{def.name}</label>
                  <select
                    value={assign?.user_id ?? ''}
                    onChange={(e) =>
                      handleSetContactRole(def.id, e.target.value || null)
                    }
                    disabled={saving}
                    className="flex-1 min-w-[180px] px-2 py-1.5 border border-border rounded bg-bgContainer text-textDefault text-sm"
                  >
                    <option value="">{t('organizations.contactRoles.noAssignment')}</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {userSelectLabel(u)}
                        {u.email && (u.first_name || u.last_name) ? ` (${u.email})` : ''}
                      </option>
                    ))}
                  </select>
                  {saving && <span className="text-xs text-textTertiary">{t('common.loading')}</span>}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRoleId(def.id);
                      setEditName(def.name);
                      setEditDescription(def.description ?? '');
                    }}
                    className="px-2 py-1 text-xs bg-bgLight text-textDefault rounded hover:bg-bgPrimary hover:text-textLight"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteCustomRole(def.id)}
                    className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  >
                    {t('common.delete')}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-border">
        {!showAddCustomRole ? (
          <button
            type="button"
            onClick={() => setShowAddCustomRole(true)}
            className="text-sm text-bgPrimary hover:underline"
          >
            {t('organizations.contactRoles.addCustomRole')}
          </button>
        ) : (
          <div className="space-y-2">
            <input
              value={customRoleCode}
              onChange={(e) => setCustomRoleCode(e.target.value)}
              placeholder={t('organizations.contactRoles.codePlaceholder') ?? 't.ex. regional_manager'}
              className="w-full px-2 py-1.5 border border-border rounded text-sm"
            />
            <input
              value={customRoleName}
              onChange={(e) => setCustomRoleName(e.target.value)}
              placeholder={t('organizations.contactRoles.roleName')}
              className="w-full px-2 py-1.5 border border-border rounded text-sm"
            />
            <input
              value={customRoleDescription}
              onChange={(e) => setCustomRoleDescription(e.target.value)}
              placeholder={t('organizations.contactRoles.description')}
              className="w-full px-2 py-1.5 border border-border rounded text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCreateCustomRole}
                disabled={submittingCustom}
                className="px-3 py-1.5 text-sm bg-bgPrimary text-textLight rounded disabled:opacity-50"
              >
                {submittingCustom ? t('common.loading') : t('organizations.contactRoles.createRole')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddCustomRole(false);
                  setCustomRoleCode('');
                  setCustomRoleName('');
                  setCustomRoleDescription('');
                }}
                className="px-3 py-1.5 text-sm bg-bgLight text-textDefault rounded"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
